'use client';

import React, { useState, useRef } from 'react';
import DrawingCanvas from './DrawingCanvas';
import BacktestConfigPanel from './BacktestConfigPanel';
import BacktestResultsPanel from './BacktestResultsPanel';
import SaveBacktestModal from './SaveBacktestModal';

export type BacktestStatus = 'idle' | 'running' | 'complete';

export interface BacktestConfigValues {
  symbol: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  initialCapital: string;
  riskPerTrade: string;
  stopLossType: string;
  takeProfitType: string;
  minPatternScore: string;
  maxOpenTrades: string;
}

export default function BacktestWorkspace() {
  const [status, setStatus] = useState<BacktestStatus>('idle');
  const [selectedTool, setSelectedTool] = useState<string>('trendline');
  const [progress, setProgress] = useState(0);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [configValues, setConfigValues] = useState<BacktestConfigValues>({
    symbol: 'NVDA',
    timeframe: '15m',
    startDate: '2025-01-01',
    endDate: '2026-08-01',
    initialCapital: '25000',
    riskPerTrade: '1',
    stopLossType: 'fixed-atr',
    takeProfitType: 'fixed-rr',
    minPatternScore: '70',
    maxOpenTrades: '3',
  });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const runBacktest = () => {
    setStatus('running');
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setStatus('complete');
          return 100;
        }
        return prev + 4;
      });
    }, 80);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* Main chart + toolbar — 3 cols */}
      <div className="xl:col-span-3 space-y-4">
        <DrawingCanvas selectedTool={selectedTool} setSelectedTool={setSelectedTool} canvasRef={canvasRef} />
        {status === 'complete' && (
          <BacktestResultsPanel
            params={configValues}
            onSave={() => setShowSaveModal(true)}
          />
        )}
      </div>

      {/* Config panel — 1 col */}
      <div className="xl:col-span-1">
        <BacktestConfigPanel
          status={status}
          progress={progress}
          onRun={runBacktest}
          onReset={() => { setStatus('idle'); setProgress(0); }}
          onConfigChange={setConfigValues}
        />
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <SaveBacktestModal
          params={configValues}
          canvasRef={canvasRef}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}